import { useCallback } from 'react';
import { useDeleteSharedPreset, usePinSharedPreset, useUpdateSharedPreset } from '@/entities/shared-presets';
import { useDeleteSystemPreset, useUpdateSystemPreset } from '@/entities/system-presets';
import { resolveServerPresetKey, type ServerPresetKeyParsers } from './serverPresetKey';
import type { TableViewSnapshot } from './types';

/**
 * Изменения серверных пресетов — системных и совместных — по ключу пресета: пресетов таблицы
 * и пользовательских дашбордов.
 *
 * Каждое действие возвращает `false`, если ключ принадлежит личному пресету: личный пресет ведут
 * настройки пользователя, и действие над ним выполняет вызывающий. Так вид пресета по ключу
 * разбирается в одном месте, а не в каждом экшене списка пресетов.
 *
 * Разборщики держать стабильными (модульная функция или `useCallback`): от них зависят действия.
 */
export const useServerPresetMutations = ({ parseSystemId, parseSharedId }: ServerPresetKeyParsers) => {
  const { mutate: updateSystemPreset } = useUpdateSystemPreset();
  const { mutate: deleteSystemPreset } = useDeleteSystemPreset();
  const { mutate: updateSharedPreset } = useUpdateSharedPreset();
  const { mutate: pinSharedPreset } = usePinSharedPreset();
  const { mutate: deleteSharedPreset } = useDeleteSharedPreset();

  /** Переименование или запись вида; `onSuccess` — по ответу сервера */
  const updateServerPreset = useCallback(
    (presetId: string, changes: { name?: string; settings?: TableViewSnapshot }, onSuccess?: () => void) => {
      const server = resolveServerPresetKey(presetId, { parseSystemId, parseSharedId });
      if (!server) return false;
      if (server.kind === 'system') updateSystemPreset({ id: server.id, ...changes }, { onSuccess });
      else updateSharedPreset({ id: server.id, ...changes }, { onSuccess });
      return true;
    },
    [updateSystemPreset, updateSharedPreset, parseSystemId, parseSharedId],
  );

  /** Закрепление в левом меню: у системного — у всех, у совместного — у текущего пользователя */
  const pinServerPreset = useCallback(
    (presetId: string, pinned: boolean) => {
      const server = resolveServerPresetKey(presetId, { parseSystemId, parseSharedId });
      if (!server) return false;
      if (server.kind === 'system') updateSystemPreset({ id: server.id, isPinned: pinned });
      else pinSharedPreset({ id: server.id, isPinned: pinned });
      return true;
    },
    [updateSystemPreset, pinSharedPreset, parseSystemId, parseSharedId],
  );

  /**
   * Удаление: системного — у всех, совместного — у всех от владельца и только из своего списка
   * от получателя; `onSuccess` — по ответу сервера
   */
  const deleteServerPreset = useCallback(
    (presetId: string, onSuccess?: () => void) => {
      const server = resolveServerPresetKey(presetId, { parseSystemId, parseSharedId });
      if (!server) return false;
      if (server.kind === 'system') deleteSystemPreset(server.id, { onSuccess });
      else deleteSharedPreset(server.id, { onSuccess });
      return true;
    },
    [deleteSystemPreset, deleteSharedPreset, parseSystemId, parseSharedId],
  );

  return { updateServerPreset, pinServerPreset, deleteServerPreset };
};
